import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/axios';

const REUSE_MS = 24 * 60 * 60 * 1000;

// Formulário de contacto self-contained (email obrigatório, telemóvel opcional c/ SMS).
export default function ListingContactForm({ listingId, onSent }) {
  const [form, setForm] = useState({ nome: '', email: '', telefone: '', mensagem: '', code: '', phone_code: '', verification_token: '', phone_verification_token: '', aceita_privacidade: false, aceita_alertas: false });
  const [emailState, setEmailState] = useState({ sending: false, verifying: false, sent: false, verified: false, err: '' });
  const [phoneState, setPhoneState] = useState({ sending: false, verifying: false, sent: false, verified: false, err: '' });
  const [state, setState] = useState({ loading: false, msg: '', err: '' });

  useEffect(() => {
    const email = form.email.trim().toLowerCase();
    if (!email || form.verification_token) return;
    try {
      const saved = JSON.parse(localStorage.getItem('mp_lead_verify') || 'null');
      if (saved && saved.email === email && saved.token && Date.now() - saved.at < REUSE_MS) {
        setForm((p) => ({ ...p, verification_token: saved.token }));
        setEmailState((s) => ({ ...s, verified: true }));
      }
    } catch (_) {}
  }, [form.email, form.verification_token]);

  const sendEmailCode = async () => {
    if (!form.email) { setEmailState((s) => ({ ...s, err: 'Preencha o email.' })); return; }
    setEmailState({ sending: true, verifying: false, sent: false, verified: false, err: '' });
    try {
      await api.post('/marketplace/verificacao/enviar-codigo', { email: form.email });
      setEmailState({ sending: false, verifying: false, sent: true, verified: false, err: '' });
    } catch (e) {
      setEmailState({ sending: false, verifying: false, sent: false, verified: false, err: e.response?.data?.detail || 'Falha ao enviar código.' });
    }
  };
  const verifyEmail = async () => {
    setEmailState((s) => ({ ...s, verifying: true, err: '' }));
    try {
      const res = await api.post('/marketplace/verificacao/confirmar', { email: form.email, code: form.code });
      setForm((p) => ({ ...p, verification_token: res.data.verification_token }));
      setEmailState((s) => ({ ...s, verifying: false, verified: true }));
    } catch (e) {
      setEmailState((s) => ({ ...s, verifying: false, verified: false, err: e.response?.data?.detail || 'Código inválido.' }));
    }
  };
  const sendSms = async () => {
    if (!form.telefone) { setPhoneState((s) => ({ ...s, err: 'Preencha o telemóvel.' })); return; }
    setPhoneState({ sending: true, verifying: false, sent: false, verified: false, err: '' });
    try {
      await api.post('/marketplace/verificacao/enviar-sms', { telefone: form.telefone });
      setPhoneState({ sending: false, verifying: false, sent: true, verified: false, err: '' });
    } catch (e) {
      setPhoneState({ sending: false, verifying: false, sent: false, verified: false, err: e.response?.data?.detail || 'Falha ao enviar SMS.' });
    }
  };
  const verifySms = async () => {
    setPhoneState((s) => ({ ...s, verifying: true, err: '' }));
    try {
      const res = await api.post('/marketplace/verificacao/confirmar-sms', { telefone: form.telefone, code: form.phone_code });
      setForm((p) => ({ ...p, phone_verification_token: res.data.verification_token }));
      setPhoneState((s) => ({ ...s, verifying: false, verified: true }));
    } catch (e) {
      setPhoneState((s) => ({ ...s, verifying: false, verified: false, err: e.response?.data?.detail || 'Código SMS inválido.' }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.verification_token) { setState({ loading: false, msg: '', err: 'Verifique o seu email.' }); return; }
    if (form.telefone && !form.phone_verification_token) { setState({ loading: false, msg: '', err: 'Indicou telemóvel — verifique-o por SMS ou deixe vazio.' }); return; }
    if (!form.aceita_privacidade) { setState({ loading: false, msg: '', err: 'Aceite a Política de Privacidade.' }); return; }
    setState({ loading: true, msg: '', err: '' });
    try {
      await api.post('/marketplace/leads', {
        publicacao_id: listingId,
        nome: form.nome, email: form.email, telefone: form.telefone, mensagem: form.mensagem,
        email_verification_token: form.verification_token,
        phone_verification_token: form.phone_verification_token,
        aceita_privacidade: form.aceita_privacidade, aceita_alertas: form.aceita_alertas,
      });
      try { localStorage.setItem('mp_lead_verify', JSON.stringify({ email: form.email.trim().toLowerCase(), token: form.verification_token, at: Date.now() })); } catch (_) {}
      setState({ loading: false, msg: 'Contacto enviado com sucesso.', err: '' });
      if (onSent) onSent();
    } catch (e) {
      setState({ loading: false, msg: '', err: e.response?.data?.detail || 'Erro ao enviar contacto.' });
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-3" data-testid="listing-contact-form">
      <input className="rounded-xl border border-slate-300 px-3 py-2" placeholder="Nome" value={form.nome} onChange={(e) => setForm((p) => ({ ...p, nome: e.target.value }))} data-testid="lp-lead-name" required />
      <input className="rounded-xl border border-slate-300 px-3 py-2" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} data-testid="lp-lead-email" required />

      <div className="space-y-2 rounded-xl border border-dashed border-emerald-300 bg-emerald-50/50 p-3">
        <p className="text-xs font-semibold text-slate-600">1) Verifique o seu email</p>
        <div className="flex gap-2">
          <button type="button" onClick={sendEmailCode} disabled={emailState.sending || emailState.verified} className="rounded-lg border border-emerald-300 px-3 py-2 text-xs font-semibold text-emerald-700 disabled:opacity-60" data-testid="lp-send-email">{emailState.verified ? '✓ Verificado' : (emailState.sending ? 'A enviar...' : (emailState.sent ? '✓ Reenviar' : 'Enviar código'))}</button>
          {!emailState.verified && <input className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Código email" value={form.code} onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))} data-testid="lp-email-code" />}
          {!emailState.verified && <button type="button" onClick={verifyEmail} disabled={emailState.verifying} className="rounded-lg border border-emerald-300 px-3 py-2 text-xs font-semibold text-emerald-700 disabled:opacity-60" data-testid="lp-verify-email">Validar</button>}
        </div>
        {emailState.err && <div className="text-xs text-red-600" data-testid="lp-email-error">{emailState.err}</div>}
      </div>

      <input className="rounded-xl border border-slate-300 px-3 py-2" placeholder="Telefone (opcional)" value={form.telefone} onChange={(e) => setForm((p) => ({ ...p, telefone: e.target.value }))} data-testid="lp-lead-phone" />
      {form.telefone.trim() && (
        <div className="space-y-2 rounded-xl border border-dashed border-cyan-300 bg-cyan-50/50 p-3" data-testid="lp-sms-block">
          <p className="text-xs font-semibold text-slate-600">2) Verifique o telemóvel (SMS) — opcional</p>
          <div className="flex gap-2">
            <button type="button" onClick={sendSms} disabled={phoneState.sending || phoneState.verified} className="rounded-lg border border-cyan-300 px-3 py-2 text-xs font-semibold text-cyan-700 disabled:opacity-60" data-testid="lp-send-sms">{phoneState.verified ? '✓' : (phoneState.sending ? 'A enviar...' : (phoneState.sent ? '✓ Reenviar' : 'Enviar SMS'))}</button>
            {!phoneState.verified && <input className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Código SMS" value={form.phone_code} onChange={(e) => setForm((p) => ({ ...p, phone_code: e.target.value }))} data-testid="lp-sms-code" />}
            {!phoneState.verified && <button type="button" onClick={verifySms} disabled={phoneState.verifying} className="rounded-lg border border-cyan-300 px-3 py-2 text-xs font-semibold text-cyan-700 disabled:opacity-60" data-testid="lp-verify-sms">Validar</button>}
          </div>
          {phoneState.err && <div className="text-xs text-red-600" data-testid="lp-sms-error">{phoneState.err}</div>}
        </div>
      )}

      <textarea className="rounded-xl border border-slate-300 px-3 py-2" rows={3} placeholder="Mensagem" value={form.mensagem} onChange={(e) => setForm((p) => ({ ...p, mensagem: e.target.value }))} data-testid="lp-lead-message" required />

      <label className="flex items-start gap-2 text-xs text-slate-600" data-testid="lp-privacy">
        <input type="checkbox" className="mt-0.5" checked={form.aceita_privacidade} onChange={(e) => setForm((p) => ({ ...p, aceita_privacidade: e.target.checked }))} data-testid="lp-privacy-checkbox" />
        <span>Li a <Link to="/privacidade" target="_blank" className="font-semibold text-emerald-600 underline">Política de Privacidade</Link> e aceito que os meus dados e mensagem sejam enviados ao anunciante.</span>
      </label>
      <label className="flex items-start gap-2 text-xs text-slate-600" data-testid="lp-alerts">
        <input type="checkbox" className="mt-0.5" checked={form.aceita_alertas} onChange={(e) => setForm((p) => ({ ...p, aceita_alertas: e.target.checked }))} data-testid="lp-alerts-checkbox" />
        <span>Quero receber alertas de novas viaturas e oportunidades TVDE por email (opcional).</span>
      </label>

      <button type="submit" disabled={state.loading} className="rounded-xl bg-emerald-600 px-4 py-2 font-semibold text-white hover:bg-emerald-700 disabled:opacity-60" data-testid="lp-lead-submit">
        {state.loading ? 'A enviar...' : 'Enviar contacto'}
      </button>
      {state.msg && <p className="text-sm font-semibold text-emerald-700" data-testid="lp-lead-success">{state.msg}</p>}
      {state.err && <p className="text-sm font-semibold text-red-600" data-testid="lp-lead-error">{state.err}</p>}
    </form>
  );
}
